(() => {
  if (window.CaffreyDeWrinkle) {
    window.CaffreyDeWrinkle.init();
    return;
  }

  const cleanups = new Map();
  const own = (element, readyKey) => {
    const controller = new AbortController();
    const callbacks = [];
    cleanups.set(element, () => {
      controller.abort();
      callbacks.forEach((callback) => callback());
      delete element.dataset[readyKey];
      cleanups.delete(element);
    });
    return {
      signal: controller.signal,
      cleanup: (callback) => callbacks.push(callback),
    };
  };

  const init = () => {
    cleanups.forEach((cleanup, element) => {
      if (!element.isConnected) cleanup();
    });
    const root = document.querySelector("[data-cf-dw-root]");

    const form = root?.querySelector("[data-cf-dw-form]");
    const price = root?.querySelector("[data-cf-dw-price]");
    const addButton = root?.querySelector("[data-cf-dw-add]");
    const floatVariant = root?.querySelector("[data-cf-dw-float-variant]");
    const floatBuy = root?.querySelector("[data-cf-dw-float-buy]");
    const floatAddButton = root?.querySelector("[data-cf-dw-float-add]");
    const hero = root?.querySelector("[data-cf-dw-hero]");
    const localNav = root?.querySelector("[data-cf-dw-local-nav]");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const textTransitions = new WeakMap();

    const updateTextSmoothly = (element, text) => {
      if (!element || element.textContent === text) return;
      if (reducedMotion || typeof element.animate !== "function") {
        element.textContent = text;
        return;
      }

      textTransitions.get(element)?.cancel();
      const outgoing = element.animate(
        [
          { opacity: 1, transform: "translateY(0)" },
          { opacity: 0, transform: "translateY(-0.35rem)" },
        ],
        { duration: 110, easing: "ease-in", fill: "forwards" },
      );
      textTransitions.set(element, outgoing);

      outgoing.finished
        .then(() => {
          if (textTransitions.get(element) !== outgoing) return;
          element.textContent = text;
          const incoming = element.animate(
            [
              { opacity: 0, transform: "translateY(0.35rem)" },
              { opacity: 1, transform: "translateY(0)" },
            ],
            {
              duration: 190,
              easing: "cubic-bezier(0.22, 1, 0.36, 1)",
              fill: "forwards",
            },
          );
          textTransitions.set(element, incoming);
          return incoming.finished;
        })
        .catch(() => {});
    };

    if (root && root.dataset.cfDwReady !== "true") {
      root.dataset.cfDwReady = "true";
      const lifecycle = own(root, "cfDwReady");
      const { signal } = lifecycle;
      lifecycle.cleanup(() => {
        [price, floatVariant, addButton].forEach((element) =>
          textTransitions.get(element)?.cancel(),
        );
      });

      localNav?.querySelectorAll('a[href^="#"]').forEach((link) => {
        link.addEventListener("click", (event) => {
          const target = document.querySelector(link.getAttribute("href"));
          if (!target) return;
          event.preventDefault();
          target.scrollIntoView({
            behavior: reducedMotion ? "auto" : "smooth",
            block: "start",
          });
        }, { signal });
      });

      form?.querySelectorAll("[data-cf-dw-variant]").forEach((input) => {
        input.addEventListener("change", () => {
          if (!input.checked) return;
          const available = input.dataset.available === "true";
          updateTextSmoothly(price, input.dataset.price || "");
          updateTextSmoothly(
            floatVariant,
            `${input.dataset.title} · ${input.dataset.price}`,
          );
          if (addButton) {
            addButton.disabled = !available;
            updateTextSmoothly(
              addButton,
              available
                ? addButton.dataset.addLabel.replace("[size]", input.dataset.title)
                : addButton.dataset.soldOutLabel,
            );
          }
          if (floatAddButton) {
            floatAddButton.disabled = !available;
            floatAddButton.textContent = available
              ? floatAddButton.dataset.addLabel
              : floatAddButton.dataset.soldOutLabel;
          }
        }, { signal });
      });

      let frame = 0;
      const updateFloatBuy = () => {
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(() => {
          if (!hero || !floatBuy) return;
          const rect = hero.getBoundingClientRect();
          const visible = rect.bottom < window.innerHeight * 0.35;
          floatBuy.classList.toggle("is-visible", visible);
          floatBuy.inert = !visible;
        });
      };

      updateFloatBuy();
      window.addEventListener("scroll", updateFloatBuy, { passive: true, signal });
      window.addEventListener("resize", updateFloatBuy, { signal });
      lifecycle.cleanup(() => window.cancelAnimationFrame(frame));
    }

    document.querySelectorAll("[data-cf-dw-formula]").forEach((formula) => {
      if (formula.dataset.cfDwFormulaReady === "true") return;
      formula.dataset.cfDwFormulaReady = "true";
      const lifecycle = own(formula, "cfDwFormulaReady");

      if (reducedMotion || typeof IntersectionObserver !== "function") {
        formula.classList.add("is-visible");
        return;
      }

      const formulaObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            formula.classList.toggle(
              "is-visible",
              entry.isIntersecting && entry.intersectionRatio >= 0.16,
            );
          });
        },
        { rootMargin: "-60px 0px -12% 0px", threshold: [0, 0.16] },
      );
      formulaObserver.observe(formula);
      lifecycle.cleanup(() => formulaObserver.disconnect());
    });

    document.querySelectorAll("[data-cf-dw-use-cases]").forEach((useCases) => {
      if (useCases.dataset.cfDwUseCasesReady === "true") return;
      useCases.dataset.cfDwUseCasesReady = "true";
      const lifecycle = own(useCases, "cfDwUseCasesReady");
      const { signal } = lifecycle;

      const track = useCases.querySelector("[data-cf-dw-use-track]");
      const cards = Array.from(
        useCases.querySelectorAll("[data-cf-dw-use-card]"),
      );
      const dotButtons = Array.from(
        useCases.querySelectorAll("[data-cf-dw-use-dot]"),
      );
      const arrowButtons = Array.from(
        useCases.querySelectorAll("[data-cf-dw-use-arrow]"),
      );
      const status = useCases.querySelector("[data-cf-dw-use-status]");
      let activeIndex = Math.max(
        0,
        Math.min(cards.length - 1, Number(useCases.dataset.active || 0)),
      );
      let scrollFrame = 0;
      let centerFrame = 0;
      let centerRun = 0;
      let resizeTimer = 0;
      let ignoreScrollUntil = 0;

      if (!track || cards.length === 0) return;
      lifecycle.cleanup(() => {
        centerRun += 1;
        window.cancelAnimationFrame(scrollFrame);
        window.cancelAnimationFrame(centerFrame);
        window.clearTimeout(resizeTimer);
      });

      const getCenteredLeft = (card) =>
        Math.max(
          0,
          card.offsetLeft - (track.clientWidth - card.offsetWidth) / 2,
        );

      const centerCard = (index) => {
        const card = cards[index];
        if (!card) return;
        track.scrollLeft = getCenteredLeft(card);
      };

      const queueCenterCard = (index, behavior = "smooth") => {
        window.cancelAnimationFrame(scrollFrame);
        window.cancelAnimationFrame(centerFrame);
        centerFrame = 0;
        const run = ++centerRun;

        const card = cards[index];
        if (!card) return;

        track.classList.add("is-programmatic");
        const shouldAnimate = !reducedMotion && behavior === "smooth";

        if (!shouldAnimate) {
          ignoreScrollUntil = window.performance.now() + 120;
          centerCard(index);
          centerFrame = window.requestAnimationFrame(() => {
            if (run !== centerRun) return;
            centerFrame = 0;
            track.classList.remove("is-programmatic");
            centerCard(index);
          });
          return;
        }

        const startedAt = window.performance.now();
        const startedLeft = track.scrollLeft;
        const duration = window.innerWidth < 750 ? 850 : 830;
        ignoreScrollUntil = startedAt + duration + 160;

        const move = (now) => {
          if (run !== centerRun) return;
          const progress = Math.min(1, (now - startedAt) / duration);
          const eased = 0.5 - Math.cos(Math.PI * progress) / 2;
          const targetLeft = getCenteredLeft(card);
          track.scrollLeft =
            startedLeft + (targetLeft - startedLeft) * eased;

          if (progress < 1) {
            centerFrame = window.requestAnimationFrame(move);
            return;
          }

          centerFrame = 0;
          centerCard(index);
          window.requestAnimationFrame(() => {
            if (run !== centerRun) return;
            track.classList.remove("is-programmatic");
            centerCard(index);
          });
        };

        centerFrame = window.requestAnimationFrame(move);
      };

      const cancelCenterMotion = () => {
        centerRun += 1;
        if (centerFrame) window.cancelAnimationFrame(centerFrame);
        centerFrame = 0;
        ignoreScrollUntil = 0;
        track.classList.remove("is-programmatic");
      };

      const setActive = (
        requestedIndex,
        { scroll = true, announce = true } = {},
      ) => {
        const safeIndex =
          ((requestedIndex % cards.length) + cards.length) % cards.length;
        activeIndex = safeIndex;
        useCases.dataset.active = String(safeIndex);

        cards.forEach((card, index) => {
          const isActive = index === safeIndex;
          card.classList.toggle("is-active", isActive);
          card.setAttribute("aria-pressed", isActive ? "true" : "false");
        });
        dotButtons.forEach((button, index) => {
          const isActive = index === safeIndex;
          button.classList.toggle("is-active", isActive);
          button.setAttribute("aria-pressed", isActive ? "true" : "false");
        });

        if (status && announce) {
          const label = cards[safeIndex].dataset.label || useCases.dataset.fallbackLabel;
          status.textContent = useCases.dataset.selectedLabel.replace("[label]", label);
        }
        if (scroll) queueCenterCard(safeIndex);
      };

      cards.forEach((card, index) => {
        card.addEventListener("click", () => setActive(index), { signal });
        card.addEventListener("focus", () => {
          if (card.matches(":focus-visible")) setActive(index);
        }, { signal });
      });

      dotButtons.forEach((button, index) => {
        button.addEventListener("click", () => setActive(index), { signal });
      });

      arrowButtons.forEach((button) => {
        button.addEventListener("click", () => {
          const direction = Number(button.dataset.cfDwUseArrow || 1);
          setActive(activeIndex + direction);
        }, { signal });
      });

      const moveWithKeyboard = (event, controls, focusTarget) => {
        if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return;
        event.preventDefault();
        const direction = event.key === "ArrowRight" ? 1 : -1;
        const nextIndex =
          ((activeIndex + direction) % cards.length + cards.length) %
          cards.length;
        setActive(nextIndex);
        controls[nextIndex]?.focus({ preventScroll: true });
        focusTarget?.(nextIndex);
      };

      track.addEventListener("keydown", (event) => {
        moveWithKeyboard(event, cards);
      }, { signal });
      useCases
        .querySelector(".cf-dw-use-cases__dots")
        ?.addEventListener("keydown", (event) => {
          moveWithKeyboard(event, dotButtons);
        }, { signal });
      track.addEventListener("pointerdown", cancelCenterMotion, {
        passive: true,
        signal,
      });

      track.addEventListener(
        "scroll",
        () => {
          if (window.performance.now() < ignoreScrollUntil) return;
          window.cancelAnimationFrame(scrollFrame);
          scrollFrame = window.requestAnimationFrame(() => {
            const trackCenter = track.scrollLeft + track.clientWidth / 2;
            let closestIndex = activeIndex;
            let closestDistance = Number.POSITIVE_INFINITY;
            cards.forEach((card, index) => {
              const cardCenter = card.offsetLeft + card.offsetWidth / 2;
              const distance = Math.abs(cardCenter - trackCenter);
              if (distance < closestDistance) {
                closestDistance = distance;
                closestIndex = index;
              }
            });
            if (closestIndex !== activeIndex) {
              setActive(closestIndex, { scroll: false, announce: false });
            }
          });
        },
        { passive: true, signal },
      );

      window.addEventListener("resize", () => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(
          () => queueCenterCard(activeIndex, "auto"),
          140,
        );
      }, { signal });

      setActive(activeIndex, { scroll: false, announce: false });
      centerFrame = window.requestAnimationFrame(() => queueCenterCard(activeIndex, "auto"));
    });
  };

  window.CaffreyDeWrinkle = { init };
  document.addEventListener("shopify:section:unload", (event) => {
    cleanups.forEach((cleanup, element) => {
      if (event.target === element || event.target.contains(element)) cleanup();
    });
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", init);
})();
